import type { CSSProperties } from "react";
import {
  COMPANION_SHAPES,
  type CompanionShapeId,
} from "../data/companion-shapes";
import type { CompanionPreferences } from "../types";

interface CompanionAvatarProps {
  companion: CompanionPreferences;
  className?: string;
}

const FALLBACK_SHAPE: CompanionShapeId = "blob";
const FALLBACK_COLOR = "#111214";

export function CompanionAvatar({
  companion,
  className = "",
}: CompanionAvatarProps) {
  const shape = Object.hasOwn(COMPANION_SHAPES, companion.shape)
    ? COMPANION_SHAPES[companion.shape as CompanionShapeId]
    : COMPANION_SHAPES[FALLBACK_SHAPE];
  const color = /^#[\da-f]{6}$/i.test(companion.color)
    ? companion.color.toLowerCase()
    : FALLBACK_COLOR;
  const faceColor = contrastColor(color);
  const face = shape.face;
  const leftEyeX = 86 + (face.leftDX || 0);
  const faceTransform = [
    `translate(${114.27 + face.x} ${114.27 + face.y})`,
    `scale(${face.sx} ${face.sy})`,
    "translate(-114.27 -114.27)",
  ].join(" ");
  const style = { "--companion-color": color } as CSSProperties;

  return (
    <span
      className={`companion-avatar ${companion.enhanced ? "companion-avatar--enhanced" : ""} ${className}`}
      style={style}
      role="img"
      aria-label="该用户的校园伙伴"
    >
      <span className="companion-avatar-halo" aria-hidden="true" />
      <span className="companion-avatar-shadow" aria-hidden="true" />
      <svg
        className="companion-avatar-art"
        viewBox="-28 -28 284.54 284.54"
        aria-hidden="true"
      >
        {companion.enhanced && (
          <path
            d={shape.path}
            fill="none"
            stroke={color}
            strokeWidth="9"
            opacity="0.2"
          />
        )}
        <path d={shape.path} fill={color} />
        <g transform={faceTransform} fill={faceColor} stroke={faceColor}>
          <ellipse
            cx={leftEyeX}
            cy="101"
            rx={7.5 * face.eye}
            ry={10 * face.eye}
            stroke="none"
          />
          <ellipse
            cx="142.5"
            cy="101"
            rx={7.5 * face.eye}
            ry={10 * face.eye}
            stroke="none"
          />
          <path
            d="M98 136 Q114.27 149 130.54 136"
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
          />
        </g>
      </svg>
    </span>
  );
}

function contrastColor(color: string): "#111214" | "#ffffff" {
  const value = color.slice(1);
  const channels = [0, 2, 4].map((offset) =>
    Number.parseInt(value.slice(offset, offset + 2), 16),
  );
  const luminance =
    channels[0] * 0.299 + channels[1] * 0.587 + channels[2] * 0.114;
  return luminance > 150 ? "#111214" : "#ffffff";
}
